import { etAuthErrorMessage } from "../../lib/et-auth-errors";
import type { ConvertibleAccountType } from "./types";

export type TrialStatus = "active" | "expired" | "revoked";

export type Trial = {
  user_id: number;
  trial_start: string;
  trial_end: string;
  length_days: number;
  status: TrialStatus;
  days_left: number;
  created_by: number;
  last_extended_at: string | null;
  extension_count: number;
  created_at: string;
  updated_at: string;
};

const j = async (r: Response) => {
  if (r.ok) return r.json();
  let err: unknown;
  try {
    err = await r.json();
  } catch {
    err = { error: r.statusText };
  }
  throw err;
};

export const apiErrorMessage = (e: unknown, fallback: string): string =>
  etAuthErrorMessage(e as { detail?: unknown; error?: unknown } | null, fallback);

export type TrialCount = {
  active: number;
  ending_soon: number;
  expired: number;
  revoked: number;
  total: number;
};

export const listTrials = (status?: TrialStatus): Promise<{ trials: Trial[] }> =>
  fetch(`/api/proxy/admin/trials${status ? `?status=${status}` : ""}`).then(j);

export const getTrialCount = (): Promise<TrialCount> =>
  fetch(`/api/proxy/admin/trials/count`).then(j);

export const getTrialsByUserIds = (
  userIds: number[],
): Promise<{ trials: Trial[] }> =>
  fetch(`/api/proxy/admin/trials/by-user-ids`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_ids: userIds }),
  }).then(j);

export const startTrial = (
  userId: number,
  lengthDays: number,
  trialStart?: string,
): Promise<Trial> =>
  fetch(`/api/proxy/admin/trials`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      length_days: lengthDays,
      ...(trialStart ? { trial_start: trialStart } : {}),
    }),
  }).then(j);

export const extendTrial = (
  userId: number,
  additionalDays: number,
): Promise<Trial> =>
  fetch(`/api/proxy/admin/trials/${userId}/extend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ additional_days: additionalDays }),
  }).then(j);

export const revokeTrial = (userId: number): Promise<Trial> =>
  fetch(`/api/proxy/admin/trials/${userId}/revoke`, { method: "POST" }).then(j);

export const reactivateTrial = (
  userId: number,
  lengthDays: number,
): Promise<Trial> =>
  fetch(`/api/proxy/admin/trials/${userId}/reactivate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ length_days: lengthDays }),
  }).then(j);

// account_role, or account_type `user` to take a demo account off its trial.
// `admin` is set from Dashboard IAM through et-auth, and `demo` only by Start
// trial; the proxy refuses both here.
export const updateUserAccount = (
  userId: string,
  patch: { account_role?: string; account_type?: ConvertibleAccountType },
): Promise<unknown> =>
  fetch(`/api/proxy/admin/users/${userId}/account`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  }).then(j);
